/**
 * useRouteCalculationToast Hook
 *
 * Manages the persistent toast notification for route calculation progress.
 * Subscribes to route store state and renders custom toast that persists
 * across tab navigation.
 *
 * @example
 * ```tsx
 * // In AppShell.tsx or global provider
 * useRouteCalculationToast();
 * ```
 */

import { useEffect, useRef, useCallback } from 'react';
import { toast as hotToast } from 'react-hot-toast';
import { useShallow } from 'zustand/shallow';
import { useRouteStore } from '../store/routeStore';
import { RouteCalculationToast } from '../components/RouteCalculationToast';

// Stable toast ID for updates
const TOAST_ID = 'route-calculation-progress';

/**
 * Hook that manages the route calculation progress toast.
 * Call this once at the app root level (e.g., AppShell).
 *
 * Features:
 * - Shows persistent toast during calculation
 * - Updates progress in real-time
 * - Shows success/error states
 * - Persists across tab navigation
 * - Auto-dismisses success toast after 5 seconds
 */
export function useRouteCalculationToast(): void {
  // Subscribe to route store state with useShallow to prevent infinite loops
  // React 19 requires stable snapshot references from useSyncExternalStore
  const {
    isCalculating,
    calculationPhase,
    calculationProgress,
    calculationElapsedSeconds,
    error,
  } = useRouteStore(
    useShallow((state) => ({
      isCalculating: state.isCalculating,
      calculationPhase: state.calculationPhase,
      calculationProgress: state.calculationProgress,
      calculationElapsedSeconds: state.calculationElapsedSeconds,
      error: state.error,
    }))
  );

  // Track if we just completed (for success state)
  const wasCalculatingRef = useRef(false);
  const isCompleteRef = useRef(false);
  const successTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Dismiss callback
  const handleDismiss = useCallback(() => {
    hotToast.dismiss(TOAST_ID);
    isCompleteRef.current = false;
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = null;
    }
  }, []);

  // Effect to manage toast lifecycle
  useEffect(() => {
    // Detect transition from calculating to complete
    if (wasCalculatingRef.current && !isCalculating && !error) {
      isCompleteRef.current = true;
    }

    // Update tracking ref
    wasCalculatingRef.current = isCalculating;

    // Show/update toast when calculating or just completed
    if (isCalculating || isCompleteRef.current || error) {
      hotToast.custom(
        (t) => (
          <RouteCalculationToast
            isCalculating={isCalculating}
            phase={calculationPhase}
            progress={calculationProgress}
            elapsedSeconds={calculationElapsedSeconds}
            error={error}
            isComplete={isCompleteRef.current}
            onDismiss={() => {
              handleDismiss();
              hotToast.dismiss(t.id);
            }}
          />
        ),
        {
          id: TOAST_ID,
          duration: Infinity, // Persistent until dismissed
          position: 'bottom-right',
        }
      );

      // Auto-dismiss success toast after 8 seconds
      if (isCompleteRef.current && !isCalculating && !error) {
        if (successTimeoutRef.current) {
          clearTimeout(successTimeoutRef.current);
        }
        successTimeoutRef.current = setTimeout(() => {
          handleDismiss();
        }, 8000);
      }

      // Auto-dismiss error toast after 10 seconds
      if (error && !isCalculating) {
        if (successTimeoutRef.current) {
          clearTimeout(successTimeoutRef.current);
        }
        successTimeoutRef.current = setTimeout(() => {
          handleDismiss();
        }, 10000);
      }
    }

    // Cleanup timeout on unmount
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, [
    isCalculating,
    calculationPhase,
    calculationProgress,
    calculationElapsedSeconds,
    error,
    handleDismiss,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      hotToast.dismiss(TOAST_ID);
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);
}

export default useRouteCalculationToast;
